import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { VERIFY_BANK_ACCOUNT, UPDATE_BANK_DETAILS, UPDATE_PAYMENT_SETTINGS } from '../graphql/mutations';
import { GET_CURRENT_USER, GET_BANK_DETAILS } from '../graphql/queries';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { fetchBanks } from '../utils/paystackUtils';
import SearchableSelect from '../components/SearchableSelect';
import { formatDate } from '../utils/dateFormat';
import { Banknote, CheckCircle, Edit2, AlertCircle, RefreshCw, BanknoteIcon } from 'lucide-react';
import { FormControlLabel, Switch } from '@mui/material';
import { Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import TransactionFeeInfo from '../components/TransactionFeeInfo';



const PaymentSettings = () => {
    const navigate = useNavigate();
    const [banks, setBanks] = useState([]);
    const [loadingBanks, setLoadingBanks] = useState(true);
    const [formData, setFormData] = useState({
        bankName: '',
        accountNumber: '',
        accountName: '',
        bankCode: '',
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isResolving, setIsResolving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);


    const { data: userData } = useQuery(GET_CURRENT_USER);
    const catalogId = userData?.getCurrentUser?.selectedCatalog?.id;
    const isEmailVerified = userData?.getCurrentUser?.isVerified || false;

    const { data: bankData, loading: bankLoading, refetch: refetchBankDetails } = useQuery(GET_BANK_DETAILS, {
        variables: { catalogId },
        skip: !catalogId
    });

    const [catalogData, setCatalogData] = useState({
        bankName: '',
        accountNumber: '',
        accountName: '',
        bankCode: '',
        allowOnlinePayment: false
    });


    const bankDetails = bankData?.catalog?.paymentSettings?.bankAccount;
    const allowOnlinePayment = bankData?.catalog?.allowOnlinePayment || false;

    const [verifyBankAccount, { loading: verifyLoading }] = useMutation(VERIFY_BANK_ACCOUNT);
    const [updateBankDetails, { loading: updateLoading }] = useMutation(UPDATE_BANK_DETAILS);

    const [updatePaymentSettings, { loading: updatePaymentLoading }] = useMutation(UPDATE_PAYMENT_SETTINGS);

    //effect to load the online payment setting
    useEffect(() => {
        if (bankData?.catalog) {
            setCatalogData(prev => ({
                ...prev,
                allowOnlinePayment: bankData.catalog.allowOnlinePayment || false
            }));
        }
    }, [bankData]);

    useEffect(() => {
        const loadBanks = async () => {
            try {
                const banksList = await fetchBanks();
                setBanks(banksList);
            } catch (error) {
                setError('Failed to load banks. Please try again later.');
            } finally {
                setLoadingBanks(false);
            }
        };

        loadBanks();
    }, []);

    useEffect(() => {
        // If bank details exist and we're not in editing mode, populate the form
        if (bankDetails && !isEditing) {
            setFormData({
                bankName: bankDetails.bankName || '',
                accountNumber: bankDetails.accountNumber || '',
                accountName: bankDetails.accountName || '',
                bankCode: bankDetails.bankCode || ''
            });
        }
    }, [bankDetails, isEditing]);

    // Add this handler
    const handleOnlinePaymentToggle = async (event) => {
        try {
            const newValue = event.target.checked;

            // Optimistically update the UI
            setCatalogData(prev => ({
                ...prev,
                allowOnlinePayment: newValue
            }));

            // Send the update to the server
            await updatePaymentSettings({
                variables: {
                    catalogId,
                    settings: {
                        allowOnlinePayment: newValue
                    }
                }
            });

            setSuccess(newValue ?
                'Online payments enabled successfully!' :
                'Online payments disabled');

            // Refetch data to ensure UI is in sync
            refetchBankDetails();

        } catch (err) {
            // Revert on error
            setCatalogData(prev => ({
                ...prev,
                allowOnlinePayment: !event.target.checked
            }));
            setError(err.message || 'An error occurred while updating payment settings');
        }
    };

    const handleAccountNumberChange = async (accountNumber) => {
        setFormData(prev => ({ ...prev, accountNumber, accountName: '' }));
        setError('');

        // Remove the automatic resolution - let the backend handle it during verification
        // The account name will be resolved when the user submits the form
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'bankName') {
            const selectedBank = banks.find(bank => bank.name === value);
            setFormData({
                ...formData,
                bankName: value,
                bankCode: selectedBank ? selectedBank.code : '',
                accountName: '' // Reset account name when bank changes
            });
        } else if (name === 'accountNumber') {
            handleAccountNumberChange(value);
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
        setError('');
    };

    const handleBankSelect = (selectedBank) => {
        setFormData({
            ...formData,
            bankName: selectedBank.name,
            bankCode: selectedBank.code,
            accountName: '' // Reset account name when bank changes
        });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Basic validation without requiring account name
            if (!formData.bankName || !formData.accountNumber || !formData.bankCode) {
                setError('Please fill in all required fields');
                return;
            }

            if (formData.accountNumber.length !== 10) {
                setError('Account number must be exactly 10 digits');
                return;
            }
            // The backend verifyBankAccount mutation will handle account resolution
            const verifyResult = await verifyBankAccount({
                variables: {
                    catalogId,
                    accountDetails: {
                        bankName: formData.bankName,
                        accountNumber: formData.accountNumber,
                        bankCode: formData.bankCode,
                        // Don't send accountName - let backend resolve it
                    },
                },
            });

            if (verifyResult.data.verifyBankAccount.success) {
                setSuccess('Bank account verified and saved successfully!');
                setIsEditing(false);
                refetchBankDetails();

                // Update form with resolved account name
                if (verifyResult.data.verifyBankAccount.accountDetails?.accountName) {
                    setFormData(prev => ({
                        ...prev,
                        accountName: verifyResult.data.verifyBankAccount.accountDetails.accountName
                    }));
                }

            } else {
                setError(verifyResult.data.verifyBankAccount.message);
            }
        } catch (err) {
            setError(err.message || 'An error occurred during verification');
        }
    };

    //check if there is bank account details(ie accountName, accountNumber, bankName, bankCode)
    const bankDetailsExist = bankDetails && bankDetails.accountName && bankDetails.accountNumber && bankDetails.bankName && bankDetails.bankCode;


    const handleEdit = () => {
        setIsEditing(true);
        setFormData({
            bankName: '',
            accountNumber: '',
            accountName: '',
            bankCode: ''
        });
    };

    if (loadingBanks || bankLoading) {
        return (
            <Layout>
                <div className="flex justify-center items-center min-h-screen p-4">
                    <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#191970]"></div>
                        <p className="text-gray-600">Loading bank details...</p>
                    </div>
                </div>
            </Layout>
        );
    }



    return (
        <Layout
            title="Bank Account Verification"
            description="Set up your payment details to receive customer payments"
        >
            <div className="p-4 sm:p-6 pb-12">
                {/* Email Verification Warning */}
                {!isEmailVerified && (
                    <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg mb-6 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium">Email Verification Required</p>
                            <p className="text-sm mt-1">
                                You need to verify your email address before you can add or update bank account details.
                                Please check your email and click the verification link.
                            </p>
                        </div>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                    {/* Show appropriate button based on bank details existence and email verification */}
                    {isEmailVerified && !isEditing && (
                        <button
                            onClick={() => {
                                setIsEditing(true);
                                // If no bank details exist, clear the form
                                if (!bankDetails) {
                                    setFormData({
                                        bankName: '',
                                        accountNumber: '',
                                        accountName: '',
                                        bankCode: ''
                                    });
                                }
                            }}
                            className="flex items-center gap-2 bg-[#191970] text-white px-4 py-2.5 rounded-lg hover:bg-[#13134d] transition-colors duration-200"
                        >
                            <Edit2 className="w-4 h-4" />
                            {bankDetailsExist ? 'Update Bank Details' : 'Add Bank Details'}
                        </button>
                    )}
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <p>{error}</p>
                    </div>
                )}

                {success && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <p>{success}</p>
                        </div>
                        <button
                            onClick={() => setSuccess('')}
                            className="text-green-500 hover:text-green-700"
                            aria-label="Dismiss message"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                )}

                {bankDetails && !isEditing ? (
                    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100 p-6">
                        <div className="flex justify-between items-start mb-6">
                            <div className="flex items-center">
                                <div className="bg-blue-100 p-2 rounded-full">
                                    <BanknoteIcon className="w-5 h-5 text-blue-600" />
                                </div>
                                <h3 className="font-semibold text-lg ml-3">Bank Account Details</h3>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-sm ${bankDetails.verificationStatus === 'VERIFIED'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                {bankDetails.verificationStatus}
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-1">
                                <p className="text-sm text-gray-500">Bank Name</p>
                                <p className="font-medium">{bankDetails.bankName}</p>
                            </div>

                            <div className="space-y-1">
                                <p className="text-sm text-gray-500">Account Number</p>
                                <p className="font-medium">{bankDetails.accountNumber}</p>
                            </div>

                            <div className="space-y-1">
                                <p className="text-sm text-gray-500">Account Name</p>
                                <p className="font-medium">{bankDetails.accountName}</p>
                            </div>

                            {bankDetails.dateVerified && (
                                <div className="space-y-1">
                                    <p className="text-sm text-gray-500">Verified On</p>
                                    <p className="font-medium">
                                        {formatDate(bankDetails.dateVerified)}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Online Payment Toggle Section */}
                        <div className="mt-6 pt-4 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="font-medium text-gray-900">Online Payments</h4>
                                    <p className="text-sm text-gray-500">Allow customers to pay for orders online</p>
                                </div>
                                <Switch
                                    checked={catalogData.allowOnlinePayment || false}
                                    onChange={handleOnlinePaymentToggle}
                                    disabled={bankDetails.verificationStatus !== 'VERIFIED'}
                                    color="primary"
                                    aria-label="Enable online payments"
                                />
                            </div>
                            {bankDetails.verificationStatus !== 'VERIFIED' && (
                                <p className="text-sm text-amber-600 mt-2">
                                    Bank verification must be completed to enable online payments
                                </p>
                            )}
                        </div>
                    </div>
                ) : isEmailVerified && (bankDetails || isEditing) ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
                        <h3 className="font-medium text-lg mb-4">
                            {bankDetails ? 'Update Bank Account' : 'Add Bank Account'}
                        </h3>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-2">Bank Name *</label>
                                <SearchableSelect
                                    options={banks}
                                    value={formData.bankName}
                                    onChange={handleBankSelect}
                                    placeholder="Select your bank"
                                    className="mt-1 px-2 py-2 block w-full bg-white rounded-md border-gray-300 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-10"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-2">Account Number *</label>
                                <input
                                    type="text"
                                    name="accountNumber"
                                    value={formData.accountNumber}
                                    onChange={handleChange}
                                    className="mt-1 px-2 py-2 block w-full bg-white rounded-md border-gray-300 shadow-sm focus:border-[#191970] focus:ring focus:ring-[#191970] focus:ring-opacity-50 h-10"
                                    required
                                    pattern="\d{10}"
                                    maxLength="10"
                                    placeholder="Enter 10-digit account number"
                                    disabled={!formData.bankCode}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-2">Account Name</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        name="accountName"
                                        value={formData.accountName}
                                        className="mt-1 px-3 py-2 block w-full bg-gray-50 rounded-md border-gray-300 shadow-sm h-10"
                                        readOnly
                                        placeholder="Account name will be verified during submission"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Account name will be automatically verified when you submit the form
                                </p>
                            </div>

                            <div className="pt-5">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    {/* Cancel button - show when editing existing details or adding new ones */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsEditing(false);
                                            // Reset form to original bank details if they exist
                                            if (bankDetails) {
                                                setFormData({
                                                    bankName: bankDetails.bankName,
                                                    accountNumber: bankDetails.accountNumber,
                                                    accountName: bankDetails.accountName,
                                                    bankCode: bankDetails.bankCode
                                                });
                                            } else {
                                                // Clear form if no existing bank details
                                                setFormData({
                                                    bankName: '',
                                                    accountNumber: '',
                                                    accountName: '',
                                                    bankCode: ''
                                                });
                                            }
                                            setError('');
                                        }}
                                        className="w-full sm:w-1/3 border border-gray-300 bg-white text-gray-700 py-2.5 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                        Cancel
                                    </button>

                                    {/* Submit button */}
                                    <button
                                        type="submit"
                                        disabled={verifyLoading || updateLoading || !formData.bankCode || !formData.accountNumber || formData.accountNumber.length !== 10}
                                        className="w-full sm:w-2/3 bg-[#191970] text-white py-2.5 rounded-lg hover:bg-[#13134d] transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {verifyLoading || updateLoading ? (
                                            <>
                                                <RefreshCw className="w-4 h-4 animate-spin" />
                                                Verifying Account...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle className="w-4 h-4" />
                                                {bankDetailsExist ? 'Update Account Details' : 'Verify & Add Account'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                ) : !isEmailVerified ? (
                    // Show placeholder when email is not verified
                    <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
                        <div className="flex flex-col items-center">
                            <div className="bg-gray-200 p-3 rounded-full mb-4">
                                <BanknoteIcon className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Bank Account Setup</h3>
                            <p className="text-gray-500 mb-4">
                                Verify your email address to add your bank account details and receive payments.
                            </p>
                            <button
                                onClick={() => navigate('/profile')}
                                className="bg-[#191970] text-white px-6 py-2.5 rounded-lg hover:bg-[#13134d] transition-colors"
                            >
                                Verify Email Address
                            </button>
                        </div>
                    </div>
                ) : (
                    // Show empty state when email is verified but no bank details and not editing
                    <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
                        <div className="flex flex-col items-center">
                            <div className="bg-gray-200 p-3 rounded-full mb-4">
                                <BanknoteIcon className="w-8 h-8 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Bank Account Details</h3>
                            <p className="text-gray-500 mb-4">
                                Add your bank account details to start receiving payments from customers.
                            </p>
                        </div>
                    </div>
                )}

                {/* Online Payment Information Box */}
                <div className="mt-4 bg-white border border-gray-100 rounded-lg p-4 shadow-sm">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                        Online Payment
                        <Tooltip title="Customer payments processed through our payment gateway will be automatically settled to your verified bank account">
                            <InfoIcon fontSize="small" className="ml-1 text-gray-500" />
                        </Tooltip>
                    </h4>

                    <div className="bg-blue-50 p-3 rounded-md">
                        <h5 className="text-sm font-medium text-gray-700">How it works</h5>
                        <ul className="mt-2 text-xs text-gray-600 space-y-2">
                            <li className="flex items-start">
                                <div className="min-w-4 h-4 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-2 mt-0.5">1</div>
                                <p>Enable online payments after verifying your bank details</p>
                            </li>
                            <li className="flex items-start">
                                <div className="min-w-4 h-4 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-2 mt-0.5">2</div>
                                <p>Customers can pay for orders directly through secure payment gateway</p>
                            </li>
                            <li className="flex items-start">
                                <div className="min-w-4 h-4 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-2 mt-0.5">3</div>
                                <p>Funds are settled to your bank account within 24 hours</p>
                            </li>
                        </ul>
                    </div>

                    <div className="mt-3 flex items-center text-xs text-gray-500">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p>Payment processing is handled securely through Paystack</p>
                    </div>
                </div>

                {/* Paystack charges information */}
                <TransactionFeeInfo subscriptionPlan={bankData?.catalog?.subscriptionPlan || 'FREE'} />


            </div>
        </Layout>
    );
};

export default PaymentSettings;
